import React from 'react';
import { Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import { ApplicationCell } from '../../ui';
import {
  formatRouteApplicationsCompact,
  formatRouteClientsCompact,
  orderRouteApplicationRows,
  type RouteApplicationJoinRow
} from './routeMultiApplicationDisplay';

function tooltipTitle(lines: string[]): React.ReactNode {
  if (lines.length <= 1) return null;
  return (
    <div>
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
    </div>
  );
}

type RouteApplicationsCompactTextProps = {
  applications: RouteApplicationJoinRow[] | null | undefined;
  /** Registry tables: keep ApplicationCell for a single linked application. */
  singleAppUseApplicationCell?: boolean;
  className?: string;
  style?: React.CSSProperties;
  emptyLabel?: string;
};

export function RouteApplicationsCompactText({
  applications,
  singleAppUseApplicationCell = false,
  className,
  style,
  emptyLabel = '—'
}: RouteApplicationsCompactTextProps): React.ReactElement {
  const ordered = orderRouteApplicationRows(applications);
  if (singleAppUseApplicationCell && ordered.length === 1 && ordered[0]?.application) {
    return <ApplicationCell application={ordered[0].application as any} />;
  }

  const summary = formatRouteApplicationsCompact(applications);
  if (summary.count === 0) {
    return (
      <span className={className} style={style}>
        {emptyLabel}
      </span>
    );
  }

  const primaryId =
    ordered[0]?.application?.id ??
    ordered[0]?.application?.applicationId ??
    ordered[0]?.applicationId ??
    null;

  const content =
    summary.count === 1 && primaryId != null ? (
      <Link to={`/applications/${primaryId}`} onClick={(e) => e.stopPropagation()} className={className} style={style}>
        {summary.display}
      </Link>
    ) : (
      <span className={className} style={style}>
        {summary.display}
      </span>
    );

  const title = tooltipTitle(summary.tooltipLines);
  return title ? <Tooltip title={title}>{content}</Tooltip> : content;
}

type RouteClientsCompactTextProps = {
  applications: RouteApplicationJoinRow[] | null | undefined;
  fallbackClient?: { id?: number | null; name?: string | null; shortName?: string | null } | null;
  className?: string;
  style?: React.CSSProperties;
  emptyLabel?: string;
  truncate?: number;
  renderPrimaryLink?: (params: { clientId: number; label: string }) => React.ReactElement | null;
};

export function RouteClientsCompactText({
  applications,
  fallbackClient,
  className,
  style,
  emptyLabel = '—',
  truncate,
  renderPrimaryLink
}: RouteClientsCompactTextProps): React.ReactElement {
  const summary = formatRouteClientsCompact(applications, fallbackClient);
  if (summary.count === 0) {
    return (
      <span className={className} style={style}>
        {emptyLabel}
      </span>
    );
  }

  let label = summary.display;
  if (truncate != null && label.length > truncate) {
    label = `${label.slice(0, truncate)}…`;
  }

  const plainContent = (
    <span className={className} style={style} title={summary.tooltipLines.join(', ')}>
      {label}
    </span>
  );

  const content =
    summary.primaryClientId != null && renderPrimaryLink
      ? renderPrimaryLink({ clientId: summary.primaryClientId, label }) ?? plainContent
      : plainContent;

  const title = tooltipTitle(summary.tooltipLines);
  if (title) {
    return <Tooltip title={title}>{content}</Tooltip>;
  }
  return content;
}
